import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaUserPlus, FaCheck, FaEye, FaEyeSlash, FaExclamationCircle } from "react-icons/fa";
import { SECURITY_KEY } from "../../globals";
import { showToast } from "../../utils/customToast";
import "./Masterdata.css";

// Helper to extract 10-digit clean mobile number
const normalizeMobile = (num) => {
  if (!num) return "";
  const cleaned = String(num).replace(/[\s\-+()]/g, "");
  return cleaned.length > 10 ? cleaned.slice(-10) : cleaned;
};

const MasterData = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    TableName: "MasterData",
    name: "",
    emailid: "",
    mobileno: "",
    Address: "",
    active: "false",
    CreationDate: "",
    Password: "",
    otp: "",
    PwdResetstring: "Nvalue",
    PwdLinkValidity: "",
    MaxCompanies: "5",
  });

  const [existingMobiles, setExistingMobiles] = useState([]);
  const [mobileError, setMobileError] = useState("");
  const [isCheckingMobile, setIsCheckingMobile] = useState(false);

  const BASE_REST_API_URL = process.env.REACT_APP_BASE_REST_API_URL;

  // Generate a 4-digit OTP
  const generateOTP = () => Math.floor(1000 + Math.random() * 9000).toString();

  // Get the current date in YYYY-MM-DD format
  const getCurrentDate = () => new Date().toISOString().split("T")[0];

  // Fetch all existing master data records to detect duplicate mobile numbers
  const fetchExistingMobiles = async () => {
    try {
      const data = new URLSearchParams();
      data.append("SecurityKey", SECURITY_KEY);
      data.append("TableName", "masterdata");
      data.append("WhereCondition", "All");
      data.append("*", "*");

      const response = await fetch(`${BASE_REST_API_URL}/Selectdata.aspx`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });

      if (response.ok) {
        const result = await response.json();
        if (Array.isArray(result?.Response)) {
          const list = result.Response.map((item) =>
            normalizeMobile(item.MobileNo || item.mobileno || item.Mobile || "")
          ).filter((m) => m && m.length === 10);
          setExistingMobiles(list);
          return list;
        }
      }
    } catch (err) {
      console.warn("Could not pre-fetch existing mobiles:", err);
    }
    return [];
  };

  useEffect(() => {
    const savedUserType = localStorage.getItem("UserType") || "0";

    setFormData((prev) => ({
      ...prev,
      CreationDate: getCurrentDate(),
      otp: generateOTP(),
      UserType: savedUserType,
    }));

    fetchExistingMobiles();
  }, []);

  // Check if a given mobile number already exists in MasterData
  const checkMobileDuplicate = async (mobileToCheck) => {
    const clean = normalizeMobile(mobileToCheck);
    if (!clean || clean.length !== 10) return false;

    // 1. Check cached list first
    if (existingMobiles.includes(clean)) {
      return true;
    }

    // 2. Fresh check against Selectdata.aspx to ensure up-to-date data
    try {
      const data = new URLSearchParams();
      data.append("SecurityKey", SECURITY_KEY);
      data.append("TableName", "masterdata");
      data.append("WhereCondition", "All");
      data.append("*", "*");

      const response = await fetch(`${BASE_REST_API_URL}/Selectdata.aspx`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });

      if (response.ok) {
        const result = await response.json();
        if (Array.isArray(result?.Response)) {
          const freshList = result.Response.map((item) =>
            normalizeMobile(item.MobileNo || item.mobileno || item.Mobile || "")
          ).filter((m) => m && m.length === 10);
          setExistingMobiles(freshList);
          if (freshList.includes(clean)) {
            return true;
          }
        }
      }
    } catch (err) {
      console.warn("Error querying Selectdata for duplicate mobile:", err);
    }

    // 3. Fallback check with Node API check-mobile if configured
    try {
      const baseApiUrl = (process.env.REACT_APP_BASE_API_URL || "").replace(/\/api\/?$/, "") + "/api";
      if (baseApiUrl) {
        const res = await fetch(`${baseApiUrl}/check-mobile`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mobile: clean }),
        });
        if (res.ok) {
          const resData = await res.json();
          if (resData && resData.exists) {
            return true;
          }
        }
      }
    } catch (e) {
      // Node backend not responding or not reachable, ignore
    }

    return false;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobileno") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        mobileno: digitsOnly,
      }));

      if (digitsOnly.length === 10) {
        if (existingMobiles.includes(digitsOnly)) {
          setMobileError("Mobile number already exists in Master Data. Duplicate not allowed.");
        } else {
          setMobileError("");
        }
      } else if (digitsOnly.length > 0 && digitsOnly.length < 10) {
        setMobileError("Mobile number must be 10 digits.");
      } else {
        setMobileError("");
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMobileBlur = async () => {
    const clean = normalizeMobile(formData.mobileno);
    if (!clean) {
      setMobileError("");
      return;
    }
    if (clean.length !== 10) {
      setMobileError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setIsCheckingMobile(true);
    const isDup = await checkMobileDuplicate(clean);
    setIsCheckingMobile(false);

    if (isDup) {
      setMobileError("Mobile number already exists in Master Data. Duplicate not allowed.");
    } else {
      setMobileError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showToast.warn("Name is required.");
      return;
    }

    const cleanMobile = normalizeMobile(formData.mobileno);
    if (!cleanMobile || cleanMobile.length !== 10) {
      setMobileError("Please enter a valid 10-digit mobile number.");
      showToast.warn("Please enter a valid 10-digit mobile number.");
      return;
    }

    setIsSubmitting(true);

    // Verify duplicate mobile number before submitting
    const isDuplicate = await checkMobileDuplicate(cleanMobile);
    if (isDuplicate) {
      setMobileError("Mobile number already exists in Master Data! Duplicate mobile numbers are not allowed.");
      showToast.error("Mobile number already exists! Duplicate mobile number cannot be added.");
      setIsSubmitting(false);
      return;
    }

    const updatedFormData = {
      ...formData,
      mobileno: cleanMobile,
      MobileNo: cleanMobile,
      CreationDate: getCurrentDate(),
      otp: generateOTP(),
    };

    const data = new URLSearchParams();
    data.append("SecurityKey", SECURITY_KEY);

    Object.keys(updatedFormData).forEach((key) => {
      data.append(key, updatedFormData[key] || "");
    });

    try {
      const response = await fetch(`${BASE_REST_API_URL}/insertdata.aspx`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "Failed to submit master data");
      }

      let jsonRes;
      try {
        jsonRes = JSON.parse(responseText);
      } catch (err) {
        // Text response
      }

      if (jsonRes && (jsonRes.Status === "Error" || jsonRes.error || jsonRes.Response?.[0]?.Status === "Error")) {
        throw new Error(jsonRes.message || jsonRes.Message || "Failed to create master record");
      }

      showToast.save("Master record created successfully!");
      navigate("/selectdata");
    } catch (error) {
      console.error("Master create error:", error);
      showToast.error("Failed to create master record: " + (error.message || "Unknown error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="masterdata_page_container">
      {/* Header & Breadcrumbs matching Dashboard theme */}
      <div className="masterdata_header_bar">
        <div className="masterdata_title_group">
          <h4>Create Master Data</h4>
          <div className="masterdata_breadcrumbs">
            <Link to="/home">Dashboard</Link>
            <span>/</span>
            <Link to="/selectdata">Master</Link>
            <span>/</span>
            <span>Add Master</span>
          </div>
        </div>
      </div>

      {/* Main Form Card */}
      <div className="masterdata_card">
        <div className="masterdata_card_header">
          <div className="masterdata_card_icon">
            <FaUserPlus />
          </div>
          <div className="masterdata_card_title_wrapper">
            <h5>New Master Record</h5>
            <p className="masterdata_card_subtitle">
              Fill in user details and allocate company limits
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="masterdata_form">
          <input type="hidden" name="TableName" value={formData.TableName} />

          {/* Name Field */}
          <div className="masterdata_field">
            <label htmlFor="master-name">
              Name <span className="required">*</span>
            </label>
            <input
              id="master-name"
              type="text"
              name="name"
              placeholder="Enter full name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          {/* Address Field */}
          <div className="masterdata_field">
            <label htmlFor="master-address">
              Address <span className="required">*</span>
            </label>
            <input
              id="master-address"
              type="text"
              name="Address"
              placeholder="Enter street / city address"
              value={formData.Address}
              onChange={handleChange}
              required
            />
          </div>

          {/* 2-column: Email ID & Mobile No */}
          <div className="masterdata_row_2col">
            <div className="masterdata_field">
              <label htmlFor="master-email">
                Email ID <span className="required">*</span>
              </label>
              <input
                id="master-email"
                type="email"
                name="emailid"
                placeholder="name@example.com"
                value={formData.emailid}
                onChange={handleChange}
                required
              />
            </div>

            <div className="masterdata_field">
              <label htmlFor="master-mobile">
                Mobile No. <span className="required">*</span>
                {isCheckingMobile && (
                  <span style={{ fontSize: "11px", color: "#008b8b", marginLeft: "8px", fontWeight: "normal" }}>
                    Checking...
                  </span>
                )}
              </label>
              <input
                id="master-mobile"
                type="tel"
                name="mobileno"
                placeholder="10-digit mobile number"
                value={formData.mobileno}
                onChange={handleChange}
                onBlur={handleMobileBlur}
                className={mobileError ? "input_error" : ""}
                maxLength={10}
                required
              />
              {mobileError && (
                <div className="masterdata_error_text">
                  <FaExclamationCircle />
                  <span>{mobileError}</span>
                </div>
              )}
            </div>
          </div>

          {/* 2-column: Password & Max Companies */}
          <div className="masterdata_row_2col">
            <div className="masterdata_field">
              <label htmlFor="master-password">Password</label>
              <div className="masterdata_input_wrapper">
                <input
                  id="master-password"
                  type={showPassword ? "text" : "password"}
                  name="Password"
                  placeholder="Enter initial password"
                  value={formData.Password}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  tabIndex="-1"
                  className="masterdata_eye_btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="masterdata_field">
              <label htmlFor="master-maxcomp">
                Max Companies <span className="required">*</span>
              </label>
              <input
                id="master-maxcomp"
                type="number"
                name="MaxCompanies"
                placeholder="Ex. 5"
                min="1"
                value={formData.MaxCompanies}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="masterdata_actions">
            <button
              type="button"
              className="masterdata_btn_cancel"
              onClick={() => navigate("/selectdata")}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="masterdata_btn_submit"
              disabled={isSubmitting}
            >
              <FaCheck />
              {isSubmitting ? "Creating..." : "Create Record"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MasterData;
